import { Box, CircularProgress, FormControl, Typography, TextField, Grid, Link, Alert } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { TWU_DECLARATIONS } from "../../util/bid/bidConstants";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import {
  fetchEmployee,
  fetchEmployeeDeclarationEventByEidAndDeclarationYear,
  addEmpDeclarationEvent,
} from "../../api/employee";
import {
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_IAM,
  IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_TWU,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants";
import {
  BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT,
  EMP_DECLARATION_CREATED_BY_TYPES,
  IAM_CONTRACT,
  TWU_CONTRACT,
} from "../../util/constants";
import {
  validateDeclaration,
  buildDeclarationEvent,
  getProjectedVacationBalanceWeeks,
  getFormattedProjectedVacationBalance,
  getFormattedProjectedFtoBalance,
} from "../../util/declarations/declarationUtils";
import StarsSelectInput from "../../components/fundamentals/StarsSelectInput";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import {
  validateBlockWeeksCount,
  validateDATWeeksCount,
  validateDatAndBlockWeeksWithTotalBalances,
  validateProjectedFTODaysCount,
  validateProjectedVacationBalanceCount,
} from "../../util/declarations/declarationValidations";
import { makeStyles } from "@mui/styles";
import { getLastUpdatedTimeString, getTimeZoneFromSwaStation } from "../../util/timeZoneService";
import ViewCommentsDialog from "../ViewCommentsDialog";
import { getUserDataFromEmployeeService } from "../../util/employeeDataRetriever";
import { getUnionContractGivenJobCode, getWorkgroupGivenJobCode } from "../../util/workgroupMapper";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "200px",
    textAlign: "left",
    paddingRight: "1rem",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  declarationInput: {
    paddingLeft: "0.1rem",
  },
  filterInputs: {
    width: "115px",
    textAlign: "left",
    backgroundColor: theme.palette.secondary.swaWhite,
  },
  lastSubmitted: {
    color: theme.palette.secondary.swaGray4,
    marginBottom: "10px",
  },
}));

const EditDeclarationDialog = (props) => {  // NOSONAR
  const classes = useStyles();

  const userSession = useSelector(selectUserSession);

  const [isEmpDeclarationLoading, setIsEmpDeclarationLoading] = useState(false);

  // Form State
  const [declaration, setDeclaration] = useState("");
  const originalDeclaration = useRef("");
  const originalDatWeeks = useRef("");
  const originalBlockWeeks = useRef("");
  const originalProjectedVacationBalance = useRef("");
  const originalProjectedFtoBalance = useRef("");
  const [declarationVersion, setDeclarationVersion] = useState("");
  const [declarationErrors, setDeclarationErrors] = useState("");
  const [datWeeks, setDatWeeks] = useState("");
  const [datWeeksErrors, setDatWeeksErrors] = useState("");
  const [blockWeeks, setBlockWeeks] = useState("");
  const [blockWeeksErrors, setBlockWeeksErrors] = useState("");
  const [projectedVacationBalance, setProjectedVacationBalance] = useState("");
  const [projectedVacationBalanceErrors, setProjectedVacationBalanceErrors] = useState("");
  const [projectedFtoBalance, setProjectedFtoBalance] = useState("");
  const [projectedFtoBalanceErrors, setProjectedFtoBalanceErrors] = useState("");
  const [employeeData, setEmployeeData] = useState("");
  const [employeeDeclarationData, setEmployeeDeclarationData] = useState(""); // We can get rid of this state once we have websocket

  //comments
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);

  // Submission State
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  useEffect(() => {
    if (props.editEmployeeData?.eid && props.declarationYear) {
      setIsEmpDeclarationLoading(true);
      fetchEmployeeDeclarationEventByEidAndDeclarationYear(props.editEmployeeData?.eid, props.declarationYear)
        .then((res) => res.json())
        .then((response) => {
          if (!response.message) {
            if (response.updateType !== "DELETE") {
              setEmployeeDeclarationData(response);
              setDeclaration(response.declaration || "");
              setDatWeeks(response.datWeeksCount || "");
              setBlockWeeks(response.blockWeeksCount || "");
              setProjectedVacationBalance(response.projectedVacationBalance || "");
              setProjectedFtoBalance(response.projectedFtoBalance || "");
              originalDeclaration.current = response.declaration || "";
              originalDatWeeks.current = response.datWeeks || "";
              originalBlockWeeks.current = response.blockWeeks || "";
              originalProjectedVacationBalance.current = response.projectedVacationBalance || "";
              originalProjectedFtoBalance.current = response.projectedFtoBalance || "";
            }
            setDeclarationVersion(response.v);
          }
        })
        .catch((err) => {
          console.error(err);
        });

      fetchEmployee(props.editEmployeeData?.eid)
        .then((res) => res.json())
        .then(async (result) => {
          if (!result.error && !result.message) {
            let filteredEmployeeData = await getUserDataFromEmployeeService(result);
            setEmployeeData(filteredEmployeeData);
          } else {
            setEmployeeData("");
          }
        })
        .catch((err) => {
          console.error(err);
        });
      setIsEmpDeclarationLoading(false);
    }
  }, [props.editEmployeeData, props.declarationYear]);

  const handleDeclarationChange = (event) => {
    const value = event.target.value;
    const declarationErrors = validateDeclaration(value);
    setDeclarationErrors(declarationErrors);
    setDeclaration(value);
  };

  const handleCommentsChange = (event) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(event.target.value);
  };

  const handleDATWeeksEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleDATWeeksBlur();
    }
  };

  const handleDATWeeksChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setDatWeeks(sanitizedInput);
    setDatWeeksErrors("");
  };

  const handleDATWeeksBlur = () => {
    const datWeekErrors = validateDATWeeksCount(datWeeks);
    if (datWeekErrors === "") {
      setDatWeeksErrors("");
    } else {
      setDatWeeksErrors(datWeekErrors);
    }
    const blockWeekErrors = validateBlockWeeksCount(blockWeeks);
    if (blockWeekErrors === "") {
      setBlockWeeksErrors("");
    } else {
      setBlockWeeksErrors(blockWeekErrors);
    }
  };

  const handleBlockWeeksEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleBlockWeeksBlur();
    }
  };

  const handleBlockWeeksChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setBlockWeeks(sanitizedInput);
    setBlockWeeksErrors("");
  };

  const handleBlockWeeksBlur = () => {
    const blockWeekErrors = validateBlockWeeksCount(blockWeeks);
    if (blockWeekErrors === "") {
      setBlockWeeksErrors("");
    } else {
      setBlockWeeksErrors(blockWeekErrors);
    }
    const datWeekErrors = validateDATWeeksCount(datWeeks);
    if (datWeekErrors === "") {
      setDatWeeksErrors("");
    } else {
      setDatWeeksErrors(datWeekErrors);
    }
  };

  const handleProjectedVacationEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleProjectedVacationEnterKeyPressed(event);
    }
  };

  const handleProjectedVacationWeeksChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setProjectedVacationBalance(sanitizedInput);
    setProjectedVacationBalanceErrors("");
  };

  const handleProjectedVacationWeeksBlur = () => {
    const projectedVacationBalErrors = validateProjectedVacationBalanceCount(
      getUnionContractGivenJobCode(employeeData.jobCode),
      employeeData.ftpt,
      projectedVacationBalance
    );
    if (projectedVacationBalErrors === "") {
      setProjectedVacationBalanceErrors("");
    } else {
      setProjectedVacationBalanceErrors(projectedVacationBalErrors);
    }
  };

  const handleProjectedFtoDaysEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleProjectedFtoDaysBlur();
    }
  };

  const handleProjectedFtoDaysChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setProjectedFtoBalance(sanitizedInput);
    setProjectedFtoBalanceErrors("");
  };

  const handleProjectedFtoDaysBlur = () => {
    const projectedFtoDaysErrors = validateProjectedFTODaysCount(projectedFtoBalance);
    if (projectedFtoDaysErrors === "") {
      setProjectedFtoBalanceErrors("");
    } else {
      setProjectedFtoBalanceErrors(projectedFtoDaysErrors);
    }
  };

  const isDeclarationChanged = () => {
    return (
      originalDeclaration.current !== declaration ||
      originalDatWeeks.current !== datWeeks ||
      originalBlockWeeks.current !== blockWeeks ||
      originalProjectedVacationBalance.current !== projectedVacationBalance ||
      originalProjectedFtoBalance.current !== projectedFtoBalance
    );
  };

  const allValidations = () => {
    const datWeeksValidationErrors = validateDATWeeksCount(datWeeks);
    if (datWeeksValidationErrors !== "") {
      setDatWeeksErrors(datWeeksValidationErrors);
    }

    const blockWeeksValidationErrors = validateBlockWeeksCount(blockWeeks);
    if (blockWeeksValidationErrors !== "") {
      setBlockWeeksErrors(blockWeeksValidationErrors);
    }

    const projectedVacationBalErrors = validateProjectedVacationBalanceCount(
      getUnionContractGivenJobCode(employeeData.jobCode),
      employeeData.ftpt,
      projectedVacationBalance
    );
    if (projectedVacationBalErrors !== "") {
      setProjectedVacationBalanceErrors(projectedVacationBalErrors);
    }

    const projectedVacationBalanceWeeks = getProjectedVacationBalanceWeeks(
      getUnionContractGivenJobCode(employeeData.jobCode),
      employeeData.ftpt,
      projectedVacationBalance
    );
    const validateWithTotalBalanceErrors = validateDatAndBlockWeeksWithTotalBalances(
      datWeeks,
      blockWeeks,
      projectedVacationBalanceWeeks
    );
    if (validateWithTotalBalanceErrors !== "") {
      setDatWeeksErrors(validateWithTotalBalanceErrors);
      setBlockWeeksErrors(validateWithTotalBalanceErrors);
    }

    if (getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT) {
      const declarationErrors = validateDeclaration(declaration);
      if (declarationErrors !== "") {
        setDeclarationErrors(declarationErrors);
      }

      const projectedFtoBalErrors = validateProjectedFTODaysCount(projectedFtoBalance);
      if (projectedFtoBalErrors !== "") {
        setProjectedFtoBalanceErrors(projectedFtoBalErrors);
      }

      return (
        datWeeksValidationErrors === "" &&
        blockWeeksValidationErrors === "" &&
        validateWithTotalBalanceErrors === "" &&
        declarationErrors === "" &&
        projectedVacationBalErrors === "" &&
        projectedFtoBalErrors === ""
      );
    } else if (getUnionContractGivenJobCode(employeeData.jobCode) === IAM_CONTRACT) {
      return (
        datWeeksValidationErrors === "" &&
        blockWeeksValidationErrors === "" &&
        validateWithTotalBalanceErrors === "" &&
        projectedVacationBalErrors === ""
      );
    } else {
      return false;
    }
  };

  const balancesValidations = () => {
    const projectedVacationBalErrors = validateProjectedVacationBalanceCount(
      getUnionContractGivenJobCode(employeeData.jobCode),
      employeeData.ftpt,
      projectedVacationBalance
    );
    if (projectedVacationBalErrors !== "") {
      setProjectedVacationBalanceErrors(projectedVacationBalErrors);
    }

    if (getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT) {
      const projectedFtoBalErrors = validateProjectedFTODaysCount(projectedFtoBalance);
      if (projectedFtoBalErrors !== "") {
        setProjectedFtoBalanceErrors(projectedFtoBalErrors);
      }

      return projectedVacationBalErrors === "" && projectedFtoBalErrors === "";
    } else if (getUnionContractGivenJobCode(employeeData.jobCode) === IAM_CONTRACT) {
      return projectedVacationBalErrors === "";
    } else {
      return false;
    }
  };

  const isBeforeDeclarationWindowStart = () => {
    const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(props.station));

    return (
      currentDateTime.year + 1 === parseInt(props.declarationYear) && currentDateTime < props.declarationWindowDates[0]
    );
  };

  const isFormValidationSuccess = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
      return false;
    } else if (comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT));
      return false;
    }

    const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(props.station));
    // do all the validations for all years except for upcoming year if declaration window not started yet to allow only balance import
    if (
      currentDateTime.year === parseInt(props.declarationYear) ||
      (currentDateTime.year + 1 === parseInt(props.declarationYear) && !isBeforeDeclarationWindowStart())
    ) {
      return allValidations();
    }

    if (currentDateTime.year + 1 === parseInt(props.declarationYear) && isBeforeDeclarationWindowStart()) {
      return balancesValidations();
    }
  };

  const onSubmit = () => {
    if (!isFormValidationSuccess()) return;

    const payload = buildDeclarationEvent(
      {
        comments: comments,
        createdBy: EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN,
        v: declarationVersion,
        eid: props.editEmployeeData.eid,
        declaration: declaration,
        blockWeeksCount: blockWeeks,
        datWeeksCount: datWeeks,
        projectedVacationBalance: projectedVacationBalance,
        projectedFtoBalance: projectedFtoBalance,
        station: employeeData.homeStation,
        unionContract: getUnionContractGivenJobCode(employeeData.jobCode),
        updateType: declarationVersion ? "UPDATE" : "CREATE",
        workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
        year: props.declarationYear,
        isAdminCreated: true,
        isBalanceImport: isBeforeDeclarationWindowStart()
          ? getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT
            ? !datWeeks && !blockWeeks && !declaration
            : !datWeeks && !blockWeeks
          : false,
      },
      userSession
    );

    setIsSaveLoading(true);
    props.setSaveErrors("");

    addEmpDeclarationEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message || result.error) {
          console.error("Error");
          resetState();
          props.setSaveErrors(result.message);
          props.setIsSuccessDialogOpen(true);
        } else if (result.pk) {
          resetState();
          props.setIsSuccessDialogOpen(true);
        }
      })
      .catch((err) => {
        console.log("err", err);
        resetState();
        props.setIsSuccessDialogOpen(true);
        props.setSaveErrors("Unexpected Exception occurred.");
      });
  };

  const handleCancelClicked = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    resetState();
  };

  const resetState = () => {
    props.onClickDialogClose();
    setEmployeeDeclarationData("");
    setEmployeeData("");
    setDeclaration("");
    originalDeclaration.current = "";
    setDeclarationVersion("");
    setDeclarationErrors("");
    setComments("");
    setCommentsError(false);
    setCommentErrorMessage("");
    setIsSaveLoading(false);
    props.setSaveErrors("");
    props.setEditEmployeeData("");
    setDatWeeks("");
    setDatWeeksErrors("");
    setBlockWeeks("");
    originalDatWeeks.current = "";
    originalBlockWeeks.current = "";
    originalProjectedVacationBalance.current = "";
    originalProjectedFtoBalance.current = "";
    setBlockWeeksErrors("");
    setProjectedVacationBalance("");
    setProjectedFtoBalance("");
    setProjectedVacationBalanceErrors("");
    setProjectedFtoBalanceErrors("");
  };

  return (
    <>
      <Dialog
        fullWidth
        open={props.isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={handleCancelClicked}
        maxWidth={"md"}
        sx={{ margin: "auto" }}
        data-testid="edit-delcaration-dialog"
      >
        <Box p={1}>
          <DialogTitle id="edit-delcaration-dialog-title">
            {props.isReadOnly ? "Employee Declaration" : "Edit Employee Declaration"}
          </DialogTitle>
          <DialogContent>
            {isEmpDeclarationLoading || !employeeData ? (
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={50} />
              </Box>
            ) : (
              <>
                {parseInt(props.declarationYear) === 2025 &&
                  getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT && (
                    <Box mb={1}>
                      <Alert severity="error">{IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_TWU()}</Alert>
                    </Box>
                  )}
                {parseInt(props.declarationYear) === 2025 &&
                  getUnionContractGivenJobCode(employeeData.jobCode) === IAM_CONTRACT && (
                    <Box mb={1}>
                      <Alert severity="error">{IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_IAM()}</Alert>
                    </Box>
                  )}
                {(employeeDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN ||
                  employeeDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_EMP) &&
                employeeDeclarationData?.createdAt &&
                employeeDeclarationData?.modifiedByFirstName &&
                employeeDeclarationData?.modifiedByLastName &&
                employeeDeclarationData?.modifiedByFirstName !== "" &&
                employeeDeclarationData?.modifiedByLastName !== "" ? (
                  <Grid className={classes.lastSubmitted}>
                    {`${LAST_UPDATED}${employeeDeclarationData.modifiedByLastName}, ${
                      employeeDeclarationData.modifiedByFirstName
                    } ${getLastUpdatedTimeString(
                      employeeDeclarationData?.createdAt,
                      getTimeZoneFromSwaStation(props.station)
                    )}`}
                    {employeeDeclarationData?.comments &&
                      employeeDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN && (
                        <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                          (View comment)
                        </Link>
                      )}
                  </Grid>
                ) : employeeDeclarationData !== "" ? (
                  <Grid className={classes.lastSubmitted}>{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString(
                    employeeDeclarationData?.createdAt,
                    getTimeZoneFromSwaStation(props.station)
                  )}`}</Grid>
                ) : (
                  ""
                )}
                {props.isReadOnly ? (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "secondary.swaGray3",
                      borderRadius: 1,
                      boxShadow: 3,
                      mb: 3,
                      pl: 7,
                      pt: 3,
                      pb: 3,
                    }}
                  >
                    <Typography variant="h6">
                      <strong>
                        {props.editEmployeeData.lastName}, {props.editEmployeeData.shortName}
                      </strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 4, color: "secondary.swaGray" }}>
                      {props.editEmployeeData.eid}
                    </Typography>

                    <Grid container spacing={2.5} alignItems={"center"}>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Home Station</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">{employeeData.homeStation}</Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Workgroup</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">{getWorkgroupGivenJobCode(employeeData.jobCode)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>FT/PT</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">{employeeData.ftpt}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Hire Date</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">{employeeData.hireDate}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Vacation Weeks</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {getFormattedProjectedVacationBalance(
                            getUnionContractGivenJobCode(employeeData.jobCode),
                            employeeData.ftpt,
                            projectedVacationBalance
                          )}
                        </Typography>
                      </Grid>
                      {getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT && (
                        <>
                          <Grid item xs={4}>
                            <Typography variant="body2">
                              <strong>Projected FTO Days</strong>
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2">
                              {getFormattedProjectedFtoBalance(projectedFtoBalance)}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Block Weeks Declared</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {blockWeeks === "" || blockWeeks === undefined ? "-" : blockWeeks}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          <strong>Dat Weeks Declared</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {datWeeks === "" || datWeeks === undefined ? "-" : datWeeks}
                        </Typography>
                      </Grid>
                      {getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT && (
                        <>
                          <Grid item xs={4}>
                            <Typography variant="body2">
                              <strong>Freeday/FTO</strong>
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2">
                              {declaration === "" || declaration === undefined ? "-" : declaration}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: "secondary.swaGray3",
                        borderRadius: 1,
                        boxShadow: 3,
                        mb: 3,
                        pl: 7,
                        pt: 3,
                        pb: 3,
                      }}
                    >
                      <Typography variant="h6">
                        <strong>
                          {props.editEmployeeData.lastName}, {props.editEmployeeData.shortName}
                        </strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 4, color: "secondary.swaGray" }}>
                        {props.editEmployeeData.eid}
                      </Typography>

                      <Grid container spacing={2.5} alignItems={"center"}>
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>Home Station</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{employeeData.homeStation}</Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>Workgroup</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{getWorkgroupGivenJobCode(employeeData.jobCode)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>FT/PT</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{employeeData.ftpt}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>Hire Date</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{employeeData.hireDate}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>Projected Vacation Hours</strong>
                          </Typography>
                        </Grid>
                        <Grid item md={8} sx={{ display: "flex", alignItems: "center" }}>
                          <div className={classes.declarationInput}>
                            <TextField
                              type="text"
                              label="Projected Vacation Hrs"
                              data-testid="choices-count-textField"
                              className={classes.inputText}
                              variant="outlined"
                              value={projectedVacationBalance}
                              onChange={handleProjectedVacationWeeksChange}
                              onBlur={handleProjectedVacationWeeksBlur}
                              onKeyUp={handleProjectedVacationEnterKeyPressed}
                              inputProps={{ inputMode: "numeric", maxLength: 3 }}
                              error={projectedVacationBalanceErrors !== ""}
                              helperText={projectedVacationBalanceErrors}
                            />
                          </div>
                          <div>
                            {projectedVacationBalance !== ""
                              ? getFormattedProjectedVacationBalance(
                                  getUnionContractGivenJobCode(employeeData.jobCode),
                                  employeeData.ftpt,
                                  projectedVacationBalance
                                )
                              : ""}
                          </div>
                        </Grid>

                        {getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT && (
                          <>
                            <Grid item xs={4}>
                              <Typography variant="body2">
                                <strong>Projected FTO Hours</strong>
                              </Typography>
                            </Grid>
                            <Grid item md={8} sx={{ display: "flex", alignItems: "center" }}>
                              <div className={classes.declarationInput}>
                                <TextField
                                  type="text"
                                  label="Projected FTO Hrs"
                                  data-testid="choices-count-textField"
                                  className={classes.inputText}
                                  variant="outlined"
                                  value={projectedFtoBalance}
                                  onChange={handleProjectedFtoDaysChange}
                                  onBlur={handleProjectedFtoDaysBlur}
                                  onKeyUp={handleProjectedFtoDaysEnterKeyPressed}
                                  inputProps={{ inputMode: "numeric", maxLength: 2 }}
                                  error={projectedFtoBalanceErrors !== ""}
                                  helperText={projectedFtoBalanceErrors}
                                />
                              </div>
                              <div>
                                {projectedFtoBalance !== "" ? getFormattedProjectedFtoBalance(projectedFtoBalance) : ""}
                              </div>
                            </Grid>
                          </>
                        )}
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>Block Weeks Declared</strong>
                          </Typography>
                        </Grid>
                        <Grid item md={8} sx={{ display: "flex" }}>
                          <div className={classes.declarationInput}>
                            <TextField
                              type="text"
                              label="Block Weeks Declared"
                              data-testid="choices-count-textField"
                              className={classes.inputText}
                              variant="outlined"
                              value={blockWeeks}
                              onChange={handleBlockWeeksChange}
                              onBlur={handleBlockWeeksBlur}
                              onKeyUp={handleBlockWeeksEnterKeyPressed}
                              inputProps={{ inputMode: "numeric", maxLength: 1 }}
                              error={blockWeeksErrors !== ""}
                              helperText={blockWeeksErrors}
                              disabled={isBeforeDeclarationWindowStart()}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">
                            <strong>DAT Weeks Declared</strong>
                          </Typography>
                        </Grid>
                        <Grid item md={8} sx={{ display: "flex" }}>
                          <div className={classes.declarationInput}>
                            <TextField
                              type="text"
                              label="DAT Weeks Declared"
                              data-testid="choices-count-textField"
                              className={classes.inputText}
                              variant="outlined"
                              value={datWeeks}
                              onChange={handleDATWeeksChange}
                              onBlur={handleDATWeeksBlur}
                              onKeyUp={handleDATWeeksEnterKeyPressed}
                              inputProps={{ inputMode: "numeric", maxLength: 1 }}
                              error={datWeeksErrors !== ""}
                              helperText={datWeeksErrors}
                              disabled={isBeforeDeclarationWindowStart()}
                            />
                          </div>
                        </Grid>
                        {getUnionContractGivenJobCode(employeeData.jobCode) === TWU_CONTRACT && (
                          <>
                            <Grid item xs={4}>
                              <Typography variant="body2">
                                <strong>Freeday/FTO</strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <FormControl sx={{ minWidth: "150px" }}>
                                <StarsSelectInput
                                  label="Freeday/FTO"
                                  menuMessage="Freeday/FTO"
                                  value={declaration}
                                  allOptions={Object.values(TWU_DECLARATIONS)}
                                  sortComparator={sortOnCaseSensitiveTexts}
                                  errors={declarationErrors}
                                  onChange={handleDeclarationChange}
                                  disabled={isBeforeDeclarationWindowStart()}
                                />
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                    <Box>
                      <TextField
                        value={comments}
                        multiline
                        fullWidth
                        rows={2}
                        label="Comments (Mandatory)"
                        placeholder="Enter reason for make this change"
                        InputLabelProps={{ shrink: true }}
                        onChange={handleCommentsChange}
                        error={commentsError}
                        helperText={commentErrorMessage}
                        disabled={!isDeclarationChanged()}
                      />
                      {comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT ? (
                        <Box sx={{ textAlign: "right", color: "primary.swaRed" }}>
                          {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "right" }}>
                          {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              id="cancelButton"
              sx={{ width: "100px" }}
              variant="outlined"
              color="primary"
              onClick={handleCancelClicked}
              disabled={isSaveLoading}
              size="large"
            >
              Cancel
            </Button>
            <Button
              sx={{ width: "180px" }}
              id="saveButton"
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={isSaveLoading || !isDeclarationChanged() || props.isReadOnly}
              size="large"
            >
              {isSaveLoading ? <CircularProgress size={24} /> : "Apply Changes"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {viewCommentsDialogOpen && (
        <ViewCommentsDialog
          isDialogOpen={viewCommentsDialogOpen}
          handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
          header={`${LAST_UPDATED}${employeeDeclarationData.modifiedByLastName}, ${employeeDeclarationData.modifiedByFirstName} on `}
          publishedTimestamp={getLastUpdatedTimeString(
            employeeDeclarationData?.createdAt,
            getTimeZoneFromSwaStation(props.station)
          )}
          message={employeeDeclarationData.comments}
        />
      )}
    </>
  );
};

EditDeclarationDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  editEmployeeData: PropTypes.object.isRequired,
  setEditEmployeeData: PropTypes.func.isRequired,
  declarationYear: PropTypes.string.isRequired,
  setSaveErrors: PropTypes.func.isRequired,
  setIsSuccessDialogOpen: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  declarationWindowDates: PropTypes.array.isRequired,
};

export default EditDeclarationDialog;
